import { DateUtils, TaskDoc, TaskRelationship } from 'common';
import { shadeColor } from 'src/global/utils/color.js';
import { ScheduleService } from 'src/global/service/ScheduleService.js';
import { Calendar } from './Calendar.js';
import { ConstraintType } from 'src/schedule/utils/ScheduleCalculate.js';

// Dependency types as stored in TaskRelationship
export enum DependencyType {
  SS = 'SS', // Start to Start
  SF = 'SF', // Start to Finish
  FS = 'FS', // Finish to Start
  FF = 'FF', // Finish to Finish
}

export class TreeNodeRelationship {
  task: Task;
  type: DependencyType;
  lag: number;

  constructor(task: Task, type: DependencyType, lag: number) {
    this.task = task;
    this.type = type;
    this.lag = lag;
  }
}

export enum TaskType {
  TASK = 'task',
  RFI_CONTAINER = 'rfis-container',
  RFI = 'rfi',
  SUBMITTAL_CONTAINER = 'submittals-container',
  SUBMITTAL = 'submittal',
  WORKFLOW_STATE = 'ws',
  MILESTONE = 'milestone',
}

export class Task {
  public doc: TaskDoc;
  public children: Task[] = [];
  public parent: Task = null;
  public level: number = 0;
  public predecessors: TreeNodeRelationship[] = [];
  public successors: TreeNodeRelationship[] = [];
  public searchCount: number; // temporary value for presenting search count in the schedule chart.
  public calendar: Calendar; // needed for dragging to update finish date.

  constructor(doc: TaskDoc, calendar: Calendar) {
    this.doc = doc;
    this.calendar = calendar;
    if (!this.doc) {
      console.error('Task constructor called with no doc', doc);
    }
  }

  get id(): number {
    return this.doc?.unique_id || 0;
  }

  get uniqueId(): number {
    return this.doc?.unique_id || 0;
  }

  get duration(): number {
    return this.doc?.duration || 0;
  }

  get type(): TaskType {
    switch (this.doc?.text1) {
      case 'rfis-container':
        return TaskType.RFI_CONTAINER;
      case 'rfi':
        return TaskType.RFI;
      case 'submittals-container':
        return TaskType.SUBMITTAL_CONTAINER;
      case 'submittal':
        return TaskType.SUBMITTAL;
      case 'status':
        return TaskType.WORKFLOW_STATE;
      default:
        return TaskType.TASK;
    }
  }

  get name(): string {
    return this.doc?.name || '';
  }

  get color(): string {
    return this.doc?.text3 || '';
  }

  set color(value: string) {
    this.doc.text3 = value;
  }

  get wbs(): string {
    return this.doc?.wbs || '';
  }

  get relatedId(): string {
    return this.doc.text2;
  }

  get canExpand(): boolean {
    return this.children.length > 0 && !this.isLinkedType;
  }

  get isLinkedType(): boolean {
    return this.type == TaskType.RFI || this.type == TaskType.SUBMITTAL;
  }

  get isLinkedTypeContainer(): boolean {
    return this.type == TaskType.RFI_CONTAINER || this.type == TaskType.SUBMITTAL_CONTAINER;
  }

  get expanded(): boolean {
    if (!this.canExpand) return false;
    return this.doc?.expanded || false;
  }

  set expanded(value: boolean) {
    if (!this.canExpand) return;
    this.doc.expanded = value;
  }

  get startDate(): Date {
    return this.doc.actual_start ? DateUtils.fromISO(this.doc.actual_start) : DateUtils.fromISO(this.doc.start);
  }

  get finishDate(): Date {
    return this.doc.actual_finish ? DateUtils.fromISO(this.doc.actual_finish) : DateUtils.fromISO(this.doc.finish);
  }

  get percentComplete(): number {
    return this.doc.percent_complete || 0;
  }

  get topLevelParent(): Task {
    if (this.level == 1) return this;
    return this.parent.topLevelParent;
  }

  get isContainer(): boolean {
    return this.children.length > 0;
  }

  get isLeaf(): boolean {
    return this.children.length === 0;
  }

  get hasDependencies(): boolean {
    return this.predecessors.length > 0 || this.successors.length > 0;
  }

  setColorIndentLevel(color: string, level: number): void {
    this.color = Task.shadeParentColor(color, level);
    this.level = level;
    this.children.forEach(child => {
      child.setColorIndentLevel(color, level + 1);
    });
  }

  public static shadeParentColor(color: string, level: number): string {
    return level % 2 == 0 ? shadeColor(color, 50 / level) : color;
  }

  clearSearchCount(): void {
    this.searchCount = 0;
  }

  incrementSearchCount(): void {
    this.searchCount = (this.searchCount || 0) + 1;
    if (this.parent) {
      this.parent.incrementSearchCount();
    }
    if (this.name.includes('Structural')) {
      console.log('incrementSearchCount', this.name, this.searchCount);
    }
  }

  addChild(child: Task): void {
    child.parent = this;
    // when adding the first child, remove the constraints that are created by default when adding a new task.
    if (this.children.length === 0 && this.doc.constraint_type == ConstraintType.START_NO_EARLIER_THAN) {
      this.doc.constraint_type = null;
      this.doc.constraint_date = null;
    }
    this.children.push(child);
    /*
    if (this.level > 1) {
      const level = this.level + 1;
      const color = this.topLevelParent.color;
      child.color = level % 2 == 0 ? shadeColor(color, 50 / level) : color;
      child.level = level;
    } else {
      child.level = 1;
    }

    if 
    child.color = ScheduleService.ROOT_TASK_COLORS[this.children.length % ScheduleService.ROOT_TASK_COLORS.length];
    */
  }

  addChildren(children: Task[]): void {
    children.forEach(child => {
      this.addChild(child);
    });
  }

  getDecendents(): Task[] {
    return this.children.flatMap(child => child.getDecendents());
  }

  sortDecendents() {
    if (this.children.length > 0) {
      this.children.forEach(child => {
        child.sortDecendents();
      });
      this.children.sort(ScheduleService.sortByDate);
    }
  }

  getNextWBS(separator: string): string {
    return this.wbs === '0' ? (this.children.length + 1).toString() : `${this.wbs}${separator}${this.children.length + 1}`;
  }

  removeChild(child: Task): void {
    this.children = this.children.filter(c => c !== child);
  }

  clone(): Task {
    const newNode = new Task(this.doc, this.calendar);
    newNode.color = this.color;
    newNode.level = this.level;
    newNode.calendar = this.calendar;
    //newNode.parent = this.parent;
    return newNode;
  }
}
